import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { INDIA_STATES_TAMIL } from '@/data/states';
import { debounceTime, finalize, fromEvent, Subject, Subscription, takeUntil } from 'rxjs';
import { AbstractControl, FormArray, FormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router';
import { INDIA_STATE_DISTRICTS } from '@/data/state-districts';
import Swal from 'sweetalert2';
import { EventBookingDetails } from '@/modals/event-details/eventBookingDetails';

@Component({
  selector: 'app-volunteer',
  templateUrl: './volunteer.component.html',
  styleUrl: './volunteer.component.scss'
})
export class VolunteerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('pageHeader', { static: true })
  pageHeader!: ElementRef<HTMLElement>;

  @ViewChild('bgWrapper', { static: true })
  bgWrapper!: ElementRef<HTMLElement>;

  statesList = INDIA_STATES_TAMIL;
  districtList: any[] = [];
  currentYear = new Date().getFullYear();
  resizeSub?: Subscription;

  eventId = 0;
  eventDetails: EventBookingDetails[];
  currentEventDetails: EventBookingDetails;

  destroy$: Subject<boolean> = new Subject<boolean>();
  remainingCount: number = 0;
  canShow: boolean = false;
  allowedDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'zohomail.com'];
  private skipUpdateRows = false;

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private renderer: Renderer2,
    private route: ActivatedRoute
  ) { }

  // ---------------- FORM INITIALIZATION ----------------
  volunteerForm = this.fb.group({
    volunteerDetailsSeq: (0),
    age: ['', Validators.required,
            Validators.min(1),
            Validators.max(120),
            Validators.pattern(/^(?:1[01][0-9]|120|[1-9][0-9]?)$/)],
    name: ['', [
      Validators.required,
      Validators.pattern(/^[A-Za-z.]+$/)
    ]],
    email: ['', [Validators.required, Validators.email, this.emailDomainValidator()]],
    phone: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)]],
    createdAt: [0],
    whatsappNumber: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)]],
    state: ['', Validators.required],
    city: [''],
    district: ['', Validators.required],
    gender: ['', Validators.required],
    pledgeStatus: ['', Validators.requiredTrue],
    userName: [''],
    password: [''],
    status: ['InActive'],
    appRequired: ['No'],
    eventId: [0],
  });

  // ------------------- ngOnInit -------------------
  ngOnInit() {

    this.eventDetailsGetAll();

    // 4️⃣ Auto copy phone → whatsapp
    this.volunteerForm.get('phone')?.valueChanges.subscribe((value) => {
      if (value && value.length === 10) {
        this.volunteerForm.get('whatsappNumber')?.setValue(value, { emitEvent: false });
      }
    });

    // 6️⃣ State → District
    this.volunteerForm.get('state')?.valueChanges.subscribe((stateValue) => {
      this.loadDistricts(stateValue);
    });
  }

  emailDomainValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const email = control.value;
            if (!email) return null; // required validator handles empty

            const parts = email.split('@');
            if (parts.length !== 2) {
                return { invalidEmail: true };
            }

            const domain = parts[1].toLowerCase();
            if (this.allowedDomains.includes(domain)) {
                return null; // valid
            }

            return { invalidDomain: true }; // invalid domain
        };
    }

  // ---------------- HEADER MARGIN ADJUST ----------------
  ngAfterViewInit() {
    this.adjustContentMargin();
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(120))
      .subscribe(() => this.adjustContentMargin());
  }

  adjustContentMargin() {
    const headerEl = this.pageHeader?.nativeElement;
    const wrapperEl = this.bgWrapper?.nativeElement;
    if (headerEl && wrapperEl) {
      const height = headerEl.offsetHeight;
      this.renderer.setStyle(wrapperEl, 'margin-top', `${height}px`);
    }
  }

  // ------------------- HELPERS -------------------
  loadDistricts(stateValue: string) {
    this.districtList = INDIA_STATE_DISTRICTS[stateValue] || [];
    this.volunteerForm.get('district')?.setValue('');
  }

  allowOnlyNumbers(event: any) {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  }

  allowOnlyName(event: KeyboardEvent) {
        const char = event.key;

        // Allow only A-Z, a-z, dot
        const pattern = /^[A-Za-z.]$/;

        if (!pattern.test(char)) {
            event.preventDefault();
        }
    }


     allowOnlyDigits(event: KeyboardEvent) {
        if (!/^[0-9]$/.test(event.key) && event.key.length === 1) {
            event.preventDefault();
        }
    }


    // Common phone handler for BOTH phoneNumber & whatsappNumber
    onPhoneInput(event: Event, controlName: string) {
        const input = event.target as HTMLInputElement;
        let val = input.value.replace(/\D/g, '');

        // must start with 6-9
        if (val.length > 0 && !/^[6-9]/.test(val)) {
            val = val.substring(1);
        }

        // limit 10 digits
        val = val.substring(0, 10);

        input.value = val;
        this.volunteerForm.get(controlName)?.setValue(val, { emitEvent: false });
    }

    // Paste handler
    onPhonePaste(event: ClipboardEvent, controlName: string) {
        event.preventDefault();
        let val = (event.clipboardData?.getData("text") || "").replace(/\D/g, "").substring(0, 10);

        if (val.length > 0 && !/^[6-9]/.test(val)) {
            val = val.replace(/^[0-5]+/, "");
        }

        const input = event.target as HTMLInputElement;
        input.value = val;

        this.volunteerForm.get(controlName)?.setValue(val, { emitEvent: false });
    }

    allowOnlyAge(event: KeyboardEvent) {
  const input = event.target as HTMLInputElement;
  const value = input.value;      // current input value
  const key = event.key;          // key pressed

  // allow control keys: backspace, delete, arrows, tab
  if (key.length > 1) return;

  // only digits allowed
  if (!/^[0-9]$/.test(key)) {
    event.preventDefault();
    return;
  }

  // first character cannot be 0
  if (value.length === 0 && key === '0') {
    event.preventDefault();
    return;
  }

  // build the new value if key is allowed
  const newValue = value + key;
  const num = parseInt(newValue, 10);

  // prevent numbers > 120
  if (num > 120) {
    event.preventDefault();
    return;
  }
}

  // -------------------- SUBMIT --------------------
  submitForm() {

    if (this.volunteerForm.invalid) {
      this.volunteerForm.markAllAsTouched();
      return;
    }

    /** +91 attach */
    const phone = this.volunteerForm.get('whatsappNumber')?.value;
    if (phone && !phone.startsWith('91')) {
      this.volunteerForm.get('whatsappNumber')?.setValue('91' + phone, { emitEvent: false });
    }

    this.volunteerForm.get('city')?.setValue(this.volunteerForm.get('district').value);

    const finalFormData = this.volunteerForm.getRawValue();
    console.log("FINAL SUBMIT:", finalFormData);
    this.saveVolunteerDetail(finalFormData);
  }



 saveVolunteerDetail(data: any) {
    this.spinner.show();

    this.appService.saveVolunteerDetail(data)
        .pipe(finalize(() => this.spinner.hide()))
        .subscribe({

            next: async (res) => {

                // ----------------------------------
                // SUCCESS ALERT
                // ----------------------------------
                await Swal.fire({
                    icon: 'success',
                    title: 'தன்னார்வலர் பதிவு வெற்றிகரமாக முடிந்தது!',
                    html: 'Volunteer Registration Saved Successfully!',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745',
                    allowOutsideClick: false
                });

                // ----------------------------------
                // WHATSAPP GROUP JOIN ALERT
                // ----------------------------------
                const groupLink = this.currentEventDetails?.volunteerWhatsappGroupLink;

                if (groupLink) {

                    const groupQrUrl =
                        'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' +
                        encodeURIComponent(groupLink);

                    await Swal.fire({
                        icon: 'info',
                        title: 'வாட்ஸ் ஆப் குழுவில் சேருங்கள்',
                        html: `
                          <p style="font-size:15px;text-align:center">
                            <b>Volunteer WhatsApp குழுவில் சேர:</b><br><br>
                            🔹 WhatsApp link கிளிக் செய்யவும் அல்லது<br>
                            🔹 QR Code scan செய்யவும்
                          </p>
                          <div style="text-align:center">
                            <img src="${groupQrUrl}" width="180" />
                          </div>
                        `,
                        confirmButtonText: 'WhatsApp குழுவில் சேர',
                        confirmButtonColor: '#25D366',
                        allowOutsideClick: false,
                        preConfirm: () => window.open(groupLink, '_blank')
                    });
                }

                // ----------------------------------
                // RESET & RELOAD
                // ----------------------------------
                this.volunteerForm.reset();
                window.location.reload();
            },

            error: (err) => {
                const msg = err.error?.message || err.message;
                this.toastr.error(msg || 'Registration failed, please try again');
            }
        });
}

  eventDetailsGetAll() {
    this.spinner.show();
    this.appService.eventDetailsGetAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        const today = new Date().setHours(0, 0, 0, 0);
        const sorted = data
          .sort((a, b) => {
            const aIsFuture = a.startDate >= today;
            const bIsFuture = b.startDate >= today;
            if (aIsFuture && !bIsFuture) return -1;
            if (!aIsFuture && bIsFuture) return 1;
            return a.startDate - b.startDate;
          });
        this.eventDetails = sorted;
        console.log(`all events::`, this.eventDetails);
        this.spinner.hide();
      }, (error: any) => {
        this.spinner.hide()
        this.toastr.error(error?.error?.message || "Something Went Wrong, Please Try Again")
      });
  }

  onHeaderImageLoad(): void {
    // Small timeout ensures browser layout has stabilized (optional)
    setTimeout(() => {
      this.adjustContentMargin();
    }, 20);
  }


selectValue(event: any) {
    const eventId = +event.target.value;
    if (!eventId) {
        // No event selected, reset
        this.remainingCount = 0;
        this.canShow = false;
        this.volunteerForm.get('eventId').setValue(null);
        return;
    }

    this.eventId = eventId;
    this.volunteerForm.get('eventId').setValue(this.eventId);

    const selectedEvent = this.eventDetails.find(item => item.eventId === this.eventId);
     this.currentEventDetails = selectedEvent;


    if (selectedEvent) {
        // Calculate remaining seats
        this.remainingCount = selectedEvent.volunteerBookingCount - selectedEvent.volunteerBookedCount;

        // Check if event is closed
        this.checkEventConditions(selectedEvent);
    } else {
        this.remainingCount = 0;
        this.canShow = true; // hide form as fallback
    }
}


  formattedDate(epoch: number): string {
    const date = new Date(epoch);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  ngOnDestroy() {
    this.resizeSub?.unsubscribe();
  }

  checkEventConditions(eventDetails: EventBookingDetails) {
    const now = Date.now();

    // Ensure endDate is number (epoch ms)
    let eventEnd = eventDetails.endDate;
    if (typeof eventEnd === 'string') {
        eventEnd = new Date(eventEnd).getTime();
    }

    const isFullyBooked = eventDetails.volunteerBookedCount >= eventDetails.volunteerBookingCount;
    const isEventOver = now > eventEnd;

    this.canShow = isFullyBooked || isEventOver;
}

}